/* eslint-disable @typescript-eslint/no-explicit-any */
// The Finance / Establishment stage that follows "Send to Establishment":
//   recovery heads fill their item checklists (Medical + Claims, then Salary)
//     -> every Establishment manager verifies the completed checklist
//     -> the Finance nodal ticks "Vetted & Verified" + "Amount Received" and
//        forwards to HR.
// One component renders the right panel for whoever is looking (recovery head,
// manager, nodal, or a read-only viewer) from the server's per-viewer view.
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { SectionCard, WarningBanner } from "./components";
import {
  FinanceTask,
  FinanceView,
  NoDuesSettlementItem,
  NoDuesSettlementDepartment,
  editFinanceAmounts,
  formatDate,
  formatINR,
  submitFinanceTask,
  verifyFinanceRequest,
  forwardFinanceToHr,
  transferFinanceTasks,
} from "../services/eSeparation.service";

type Confirm = (
  message: string,
  opts?: { title?: string; confirmLabel?: string; tone?: "primary" | "danger" | "success" | "warning" }
) => Promise<boolean>;

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  LOCKED: { bg: "#EEEDEB", color: "#78746D", label: "Locked" },
  PENDING: { bg: "#FFF1DE", color: "#BA7517", label: "Pending" },
  SUBMITTED: { bg: "#E4F7E8", color: "#049123", label: "Submitted" },
  VERIFIED: { bg: "#E4F7E8", color: "#049123", label: "Verified" },
};

const Pill: React.FC<{ status: string }> = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.PENDING;
  return (
    <span
      className="px-3 py-1 rounded-pill fw-medium"
      style={{ fontSize: 12, background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
};

/* ───────────────────────── one recovery head's checklist ───────────────────────── */
const TaskCard: React.FC<{
  task: FinanceTask;
  isNodal: boolean;
  canEdit: boolean;
  closed: boolean;
  confirm: Confirm;
  onChanged: () => void;
}> = ({ task, isNodal, canEdit, closed, confirm, onChanged }) => {
  const [open, setOpen] = useState(task.can_submit || task.status === "PENDING");
  const [draft, setDraft] = useState<Record<number, { amount: string; comments: string }>>({});
  const [busy, setBusy] = useState(false);
  const [transferTo, setTransferTo] = useState("");

  useEffect(() => {
    const d: Record<number, { amount: string; comments: string }> = {};
    task.items.forEach((i) => {
      d[i.id] = { amount: i.amount_due ? String(i.amount_due) : "", comments: i.comments || "" };
    });
    setDraft(d);
  }, [task.id, task.status, task.items]);

  const editable = task.can_submit || canEdit;
  const draftTotal = task.items.reduce((s, i) => s + (Number(draft[i.id]?.amount) || 0), 0);

  const submit = async () => {
    for (const i of task.items) {
      const a = draft[i.id]?.amount;
      if (a !== "" && a !== undefined && (isNaN(Number(a)) || Number(a) < 0)) {
        return toast.warning(`Item ${i.sn}: enter a valid amount (0 or more).`);
      }
    }
    if (
      !(await confirm(
        `Submit "${task.group_name}" with a total of ${formatINR(draftTotal)}?`,
        { title: "Submit Checklist", confirmLabel: "Submit", tone: "primary" }
      ))
    )
      return;
    setBusy(true);
    const res = await submitFinanceTask({
      task_id: task.id,
      items: task.items.map((i) => ({
        item_id: i.id,
        amount_due: Number(draft[i.id]?.amount) || 0,
        comments: draft[i.id]?.comments?.trim() || undefined,
      })),
    });
    setBusy(false);
    if (res.success) {
      toast.success("Checklist submitted");
      onChanged();
    } else toast.error(res.message || "Failed to submit the checklist");
  };

  const saveChanges = async () => {
    for (const i of task.items) {
      const a = draft[i.id]?.amount;
      if (a !== "" && a !== undefined && (isNaN(Number(a)) || Number(a) < 0)) {
        return toast.warning(`Item ${i.sn}: enter a valid amount (0 or more).`);
      }
    }
    setBusy(true);
    const res = await editFinanceAmounts({
      kind: "CHECKLIST",
      entries: task.items.map((i) => ({
        item_id: i.id,
        amount_due: Number(draft[i.id]?.amount) || 0,
        comments: draft[i.id]?.comments?.trim() || "",
      })),
    });
    setBusy(false);
    if (res.success) {
      toast.success(res.data?.updated ? "Amounts updated" : "No changes to save");
      onChanged();
    } else toast.error(res.message || "Failed to update the amounts");
  };

  const transfer = async () => {
    if (!transferTo.trim()) return toast.warning("Enter the employee number to transfer to.");
    setBusy(true);
    const res = await transferFinanceTasks({ task_ids: [task.id], to_emp_id: transferTo.trim() });
    setBusy(false);
    if (res.success) {
      toast.success("Checklist transferred");
      setTransferTo("");
      onChanged();
    } else toast.error(res.message || "Failed to transfer");
  };

  return (
    <div className="p-3 rounded-3 border bg-light mb-3" style={{ borderLeft: "4px solid #993C1D" }}>
      <div
        className="d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <div>
          <div className="fw-bold text-dark">
            <i className={`ti ${open ? "ti-chevron-down" : "ti-chevron-right"} me-1`} />
            {task.group_name}
            <span className="badge bg-secondary-subtle text-secondary border ms-2">Stage {task.stage}</span>
          </div>
          <small className="text-muted">
            Recovery head: {task.assignee_name || "—"} ({task.assignee_emp_id})
            {task.submitted_at ? ` · submitted ${formatDate(task.submitted_at)}` : ""}
          </small>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className="fw-semibold" style={{ color: "#993C1D" }}>
            {formatINR(editable ? draftTotal : task.total)}
          </span>
          <Pill status={task.status} />
        </div>
      </div>

      {open && (
        <div className="mt-3">
          {task.status === "LOCKED" && (
            <WarningBanner
              type="info"
              message="This checklist opens once every Medical and Claims recovery head has submitted."
            />
          )}
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-2 bg-white">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>S.N.</th>
                  <th>Items to be checked</th>
                  <th style={{ width: 150 }} className="text-end">Amt. Due (₹)</th>
                  <th style={{ width: "32%" }}>Comments</th>
                </tr>
              </thead>
              <tbody>
                {task.items.map((i) => (
                  <tr key={i.id}>
                    <td>{i.sn}</td>
                    <td>{i.item_name}</td>
                    {editable ? (
                      <>
                        <td>
                          <input
                            className="form-control form-control-sm text-end"
                            inputMode="decimal"
                            placeholder="0"
                            value={draft[i.id]?.amount ?? ""}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                [i.id]: { ...(d[i.id] || { amount: "", comments: "" }), amount: e.target.value },
                              }))
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            placeholder="Comment"
                            value={draft[i.id]?.comments ?? ""}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                [i.id]: { ...(d[i.id] || { amount: "", comments: "" }), comments: e.target.value },
                              }))
                            }
                          />
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="text-end">{formatINR(i.amount_due)}</td>
                        <td className="text-muted small">{i.comments || "—"}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {task.can_submit && (
            <div className="d-flex justify-content-end">
              <button className="btn btn-primary" disabled={busy} onClick={submit}>
                <i className="ti ti-send me-1" />
                {busy ? "Submitting…" : "Submit Checklist"}
              </button>
            </div>
          )}
          {!task.can_submit && canEdit && (
            <div className="d-flex justify-content-end">
              <button className="btn btn-outline-primary" disabled={busy} onClick={saveChanges}>
                <i className="ti ti-device-floppy me-1" />
                {busy ? "Saving…" : "Save Changes"}
              </button>
            </div>
          )}
          {isNodal && task.status !== "SUBMITTED" && !closed && (
            <div className="d-flex align-items-center gap-2 mt-2 pt-2 border-top">
              <small className="text-muted">Transfer this checklist to</small>
              <input
                className="form-control form-control-sm"
                style={{ maxWidth: 180 }}
                inputMode="numeric"
                placeholder="Employee no."
                value={transferTo}
                onChange={(e) => setTransferTo(e.target.value)}
              />
              <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={transfer}>
                <i className="ti ti-arrows-exchange me-1" />
                Transfer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ───────────── amounts recorded by the clearing departments ───────────── */
const DepartmentAmounts: React.FC<{
  departments: NoDuesSettlementDepartment[];
  shortNotice: NoDuesSettlementItem[];
  canEdit: boolean;
  onChanged: () => void;
}> = ({ departments, shortNotice, canEdit, onChanged }) => {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const d: Record<string, string> = {};
    departments.forEach((dep) =>
      dep.items.forEach((i) => (d[`D${i.id}`] = i.amount_due ? String(i.amount_due) : ""))
    );
    shortNotice.forEach((i) => (d[`S${i.id}`] = i.amount_due ? String(i.amount_due) : ""));
    setDraft(d);
  }, [departments, shortNotice]);

  const rows = departments.filter((d) => d.items.length > 0);
  if (!rows.length && !shortNotice.length) return null;

  const save = async () => {
    const num = (k: string) => (draft[k] === "" || draft[k] === undefined ? 0 : Number(draft[k]));
    for (const k of Object.keys(draft)) {
      if (isNaN(num(k)) || num(k) < 0) return toast.warning("Enter valid amounts (0 or more).");
    }
    const dept = departments.flatMap((dep) =>
      dep.items
        .filter((i) => num(`D${i.id}`) !== Number(i.amount_due || 0))
        .map((i) => ({ item_id: i.id, amount_due: num(`D${i.id}`) }))
    );
    const sn = shortNotice
      .filter((i) => num(`S${i.id}`) !== Number(i.amount_due || 0))
      .map((i) => ({ item_id: i.id, amount_due: num(`S${i.id}`) }));
    if (!dept.length && !sn.length) return toast.info("No changes to save.");
    setBusy(true);
    const results = await Promise.all([
      dept.length ? editFinanceAmounts({ kind: "DEPARTMENT", entries: dept }) : null,
      sn.length ? editFinanceAmounts({ kind: "SHORT_NOTICE", entries: sn }) : null,
    ]);
    setBusy(false);
    const failed = results.find((r) => r && !r.success);
    if (failed) toast.error(failed.message || "Failed to update the amounts");
    else {
      toast.success("Amounts updated");
      onChanged();
    }
  };

  return (
    <SectionCard
      index={3}
      title="Department-recorded Amounts"
      subtitle={
        canEdit
          ? "Recorded by the clearing departments — Finance can correct any amount."
          : "Recorded by the clearing departments."
      }
    >
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-2">
          <thead>
            <tr>
              <th>Department</th>
              <th>Item</th>
              <th className="text-end" style={{ width: 160 }}>Amount Due (₹)</th>
            </tr>
          </thead>
          <tbody>
            {rows.flatMap((dep) =>
              dep.items.map((i) => (
                <tr key={`D${i.id}`}>
                  <td>{dep.department_name}</td>
                  <td>
                    {i.item_name}
                    {i.comments ? <div className="small text-muted">{i.comments}</div> : null}
                  </td>
                  <td className="text-end">
                    {canEdit ? (
                      <input
                        className="form-control form-control-sm text-end"
                        inputMode="decimal"
                        placeholder="0"
                        value={draft[`D${i.id}`] ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, [`D${i.id}`]: e.target.value }))}
                      />
                    ) : (
                      formatINR(Number(i.amount_due))
                    )}
                  </td>
                </tr>
              ))
            )}
            {shortNotice.map((i) => (
              <tr key={`S${i.id}`}>
                <td>{i.source_department_name}</td>
                <td>{i.item_name}</td>
                <td className="text-end">
                  {canEdit ? (
                    <input
                      className="form-control form-control-sm text-end"
                      inputMode="decimal"
                      placeholder="0"
                      value={draft[`S${i.id}`] ?? ""}
                      onChange={(e) => setDraft((d) => ({ ...d, [`S${i.id}`]: e.target.value }))}
                    />
                  ) : (
                    formatINR(Number(i.amount_due))
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="d-flex justify-content-end">
          <button className="btn btn-outline-primary" disabled={busy} onClick={save}>
            <i className="ti ti-device-floppy me-1" />
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      )}
    </SectionCard>
  );
};

/* ───────────────────────────── the whole flow ───────────────────────────── */
const FinanceRecoveryPanels: React.FC<{
  requestId: string;
  finance: FinanceView;
  deptDues: NoDuesSettlementItem[];
  departments: NoDuesSettlementDepartment[];
  closed: boolean;
  confirm: Confirm;
  onChanged: () => void;
}> = ({ requestId, finance, deptDues, departments, closed, confirm, onChanged }) => {
  const roles = finance.roles || { nodal: false, recovery_head: false, manager: false };
  const tasks = finance.tasks || [];
  const reviews = finance.reviews || [];
  const prog = finance.progress;
  const canEdit = !!finance.can_edit_amounts && !closed;
  const so = finance.signoff;

  const [verifyComment, setVerifyComment] = useState("");
  const [vetted, setVetted] = useState(false);
  const [received, setReceived] = useState(false);
  const [nodalComment, setNodalComment] = useState("");
  const [busy, setBusy] = useState(false);

  const stages = [
    { n: 1, label: "Medical & Claims" },
    { n: 2, label: "Salary" },
  ];

  const verify = async () => {
    if (!(await confirm("Verify this request's completed checklist?", { title: "Verify", confirmLabel: "Verify", tone: "primary" })))
      return;
    setBusy(true);
    const res = await verifyFinanceRequest({ request_id: requestId, comments: verifyComment.trim() || undefined });
    setBusy(false);
    if (res.success) {
      toast.success("Verification recorded");
      setVerifyComment("");
      onChanged();
    } else toast.error(res.message || "Failed to verify");
  };

  const forward = async () => {
    if (!vetted || !received)
      return toast.warning('Tick both "Vetted & Verified" and "Amount Received" to forward.');
    if (
      !(await confirm("Forward this No-Dues to HR? This completes the Finance stage.", {
        title: "Forward to HR",
        confirmLabel: "Forward",
        tone: "success",
      }))
    )
      return;
    setBusy(true);
    const res = await forwardFinanceToHr({
      request_id: requestId,
      vetted_verified: vetted,
      amount_received: received,
      comments: nodalComment.trim() || undefined,
    });
    setBusy(false);
    if (res.success) {
      toast.success("Forwarded to HR");
      onChanged();
    } else toast.error(res.message || "Failed to forward");
  };

  const myReview = reviews.find((r) => r.can_verify);

  return (
    <>
      {/* Progress */}
      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
        <div className="card-body">
          <div className="row g-2">
            {[
              {
                label: "Recovery heads",
                value: `${prog?.tasks_submitted ?? 0}/${prog?.tasks_total ?? 0} submitted`,
                done: !!finance.all_tasks_submitted,
              },
              {
                label: "Establishment managers",
                value: `${prog?.reviews_verified ?? 0}/${prog?.reviews_total ?? 0} verified`,
                done: !!finance.all_verified,
              },
              {
                label: "Finance nodal sign-off",
                value: so?.nodal_forwarded_at ? "Forwarded to HR" : finance.can_forward ? "Ready to forward" : "Waiting",
                done: !!so?.nodal_forwarded_at,
              },
            ].map((s) => (
              <div className="col-md-4" key={s.label}>
                <div
                  className="p-2 rounded-3 d-flex align-items-center gap-2"
                  style={{
                    border: `1px solid ${s.done ? "#049123" : "rgba(0,0,0,0.08)"}`,
                    background: s.done ? "#E4F7E8" : "#fff",
                  }}
                >
                  <i
                    className={`ti ${s.done ? "ti-circle-check" : "ti-clock"}`}
                    style={{ color: s.done ? "#049123" : "#BA7517", fontSize: 20 }}
                  />
                  <div>
                    <div className="small fw-semibold">{s.label}</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>{s.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recovery checklists */}
      <SectionCard
        index={1}
        title="Recovery Checklists"
        subtitle={
          roles.recovery_head && !roles.nodal && !roles.manager
            ? "Your assigned items — enter the amount due and a comment for each, then submit."
            : "Item-wise recovery recorded by each recovery head."
        }
      >
        {stages.map((st) => {
          const group = tasks.filter((t) => t.stage === st.n);
          if (!group.length) return null;
          return (
            <div key={st.n} className="mb-3">
              <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 1.1 }}>
                Stage {st.n} — {st.label}
              </div>
              {group.map((t) => (
                <TaskCard
                  key={t.id}
                  task={t}
                  isNodal={roles.nodal}
                  canEdit={canEdit}
                  closed={closed}
                  confirm={confirm}
                  onChanged={onChanged}
                />
              ))}
            </div>
          );
        })}
        {!tasks.length && <WarningBanner type="info" message="No checklists to show." />}
      </SectionCard>

      {/* Establishment managers */}
      <SectionCard
        index={2}
        title="Establishment Verification"
        subtitle="Every Establishment manager verifies the completed checklist and may comment."
      >
        {!finance.all_tasks_submitted && (
          <WarningBanner
            type="info"
            message="Verification opens once every recovery head has submitted their checklist."
          />
        )}
        <div className="d-flex flex-column gap-2">
          {reviews.map((r) => (
            <div key={r.id} className="p-3 rounded-3 border bg-light">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <div className="fw-semibold">{r.label}</div>
                  <small className="text-muted">
                    {r.manager_name || "—"} ({r.manager_emp_id})
                    {r.acted_at ? ` · ${formatDate(r.acted_at)}` : ""}
                  </small>
                </div>
                <Pill status={r.status} />
              </div>
              {r.comments && (
                <div className="small mt-2 text-secondary bg-white p-2 rounded border fst-italic">
                  &quot;{r.comments}&quot;
                </div>
              )}
            </div>
          ))}
        </div>
        {myReview && (
          <div className="mt-3 p-3 rounded-3" style={{ background: "#FFF8EC", border: "1px solid #f0d9a8" }}>
            <div className="fw-semibold mb-2">Your verification</div>
            <textarea
              className="form-control mb-2"
              rows={2}
              placeholder="Comment (optional)"
              value={verifyComment}
              onChange={(e) => setVerifyComment(e.target.value)}
            />
            <button className="btn btn-primary" disabled={busy} onClick={verify}>
              <i className="ti ti-circle-check me-1" />
              {busy ? "Saving…" : "Verify"}
            </button>
          </div>
        )}
      </SectionCard>

      {/* Amounts the clearing departments recorded */}
      <DepartmentAmounts
        departments={departments}
        shortNotice={deptDues.filter((d) => d.source_department_id === 0)}
        canEdit={canEdit}
        onChanged={onChanged}
      />

      {/* Nodal sign-off */}
      <SectionCard
        index={4}
        title="Finance Nodal Sign-off"
        subtitle="Both boxes are mandatory before forwarding to HR."
      >
        {so?.nodal_forwarded_at ? (
          <div className="p-3 rounded-3" style={{ background: "#E4F7E8", border: "1px solid #b7dfc2" }}>
            <div className="fw-semibold" style={{ color: "#1B7A3D" }}>
              <i className="ti ti-circle-check me-1" />
              Forwarded to HR on {formatDate(so.nodal_forwarded_at)}
            </div>
            <div className="small mt-1">
              <i className={`ti ${so.vetted_verified ? "ti-square-check" : "ti-square"} me-1`} />
              Vetted &amp; Verified &nbsp;·&nbsp;
              <i className={`ti ${so.amount_received ? "ti-square-check" : "ti-square"} me-1`} />
              Amount Received
            </div>
            {so.nodal_comments && (
              <div className="small mt-2 fst-italic">&quot;{so.nodal_comments}&quot;</div>
            )}
          </div>
        ) : roles.nodal ? (
          <>
            {!finance.can_forward && (
              <WarningBanner
                type="info"
                message="You can forward once every recovery head has submitted and all Establishment managers have verified."
              />
            )}
            <div className="d-flex flex-column gap-2 mb-3">
              <label className="d-flex align-items-center gap-2" style={{ cursor: finance.can_forward ? "pointer" : "not-allowed" }}>
                <input
                  type="checkbox"
                  className="form-check-input mt-0"
                  disabled={!finance.can_forward}
                  checked={vetted}
                  onChange={(e) => setVetted(e.target.checked)}
                />
                <span className="fw-semibold">Vetted &amp; Verified</span>
                <span className="text-danger">*</span>
              </label>
              <label className="d-flex align-items-center gap-2" style={{ cursor: finance.can_forward ? "pointer" : "not-allowed" }}>
                <input
                  type="checkbox"
                  className="form-check-input mt-0"
                  disabled={!finance.can_forward}
                  checked={received}
                  onChange={(e) => setReceived(e.target.checked)}
                />
                <span className="fw-semibold">Amount Received</span>
                <span className="text-danger">*</span>
              </label>
            </div>
            <textarea
              className="form-control mb-3"
              rows={3}
              placeholder="Comment"
              disabled={!finance.can_forward}
              value={nodalComment}
              onChange={(e) => setNodalComment(e.target.value)}
            />
            <button
              className="btn btn-success"
              disabled={busy || !finance.can_forward || !vetted || !received}
              onClick={forward}
            >
              <i className="ti ti-arrow-forward-up me-1" />
              {busy ? "Forwarding…" : "Forward to HR"}
            </button>
          </>
        ) : (
          <div className="text-muted small">
            The Finance nodal officer signs off here once every manager has verified, then forwards to HR.
          </div>
        )}
      </SectionCard>
    </>
  );
};

export default FinanceRecoveryPanels;
